// Story 8.10 (ADO-2019), Q2: the send guard. A manual send of a cart an HHT order can add to
// (a cart at Ambient 21, Chill 25 or Alcohol 85, Fruit & Veg included, DV-58; from phase 4 the
// Customer's Default VC cart, DV-73) while HHT orders wait asks first: "Send anyway" or "Review
// HHT orders". It never blocks the send (D-14): the waiting orders stay waiting for the next
// order. Every manual send path asks through here: the Order Review's two Submit buttons
// (`useSubmitOrder`) and the open carts' "Submit now" on /choose. Each page mounts the dialog
// (`HhtSendGuardDialog`), which reads this store.
//
// Whether HHT orders wait is the cheap waiting count (`useHhtWaitingCountStore`, DV-39) as it
// stands when the button is pressed; which depot a cart is at, the session's ordering entries.
// Which cart is the Default VC cart is the server's answer (`getHhtDefaultValueCentreCart`),
// asked only when it matters: orders wait and the cart is a `DEPOT` cart, keyed by an account,
// outside 21, 25 and 85. A failed lookup is logged and the question is asked, so a send is never
// made unasked on a guess (DV-73). Any press while the lookup runs is ignored, as the page ignores
// a press while a send runs, so two carts never send concurrently; a lookup that lands after
// `reset` (the page went, or sign-out) neither asks nor sends.
import { create } from 'zustand';
import type { CartTarget } from '../api/cart-target';
import { getHhtDefaultValueCentreCart } from '../api/hht-orders.client';
import type { OrderingEntry } from '../api/ordering-choices.client';
import { useSessionStore } from '../auth/useSessionStore';
import { useHhtWaitingCountStore } from './useHhtWaitingCountStore';

/** The environment depots an HHT order adds to (DV-58). */
export const HHT_SEND_GUARD_DEPOT_CODES: ReadonlySet<string> = new Set(['21', '25', '85']);

/** Whether a cart at `depotId` is one an HHT order could have added to. */
export function isHhtGuardedDepot(entries: readonly OrderingEntry[], depotId: string): boolean {
  return entries.some(
    (entry) =>
      entry.depotId === depotId &&
      !entry.isValueCentre &&
      HHT_SEND_GUARD_DEPOT_CODES.has(entry.depotCode.trim().replace(/^0+(?=\d)/, '')),
  );
}

/** The question on screen: how many orders wait, and the send to make on "Send anyway". */
export interface HhtSendGuardRequest {
  readonly waitingCount: number;
  readonly sendAnyway: () => void;
  /** Called when the Shopper closes the question without sending (Close, Escape, the scrim). */
  readonly onDismiss?: () => void;
}

interface HhtSendGuardState {
  readonly request: HhtSendGuardRequest | null;
  /**
   * A manual send of `cart`: asks first when HHT orders wait and the cart is at an HHT depot or
   * is the Default VC cart, else sends. Resolves to whether it asked. A cart at an HHT depot is
   * asked about at once; a Value Centre's `DEPOT` cart waits for the Default VC cart lookup.
   */
  guard: (
    cart: CartTarget,
    send: () => void,
    options?: { readonly onDismiss?: () => void },
  ) => Promise<boolean>;
  /** "Send anyway": closes the question and sends. */
  sendAnyway: () => void;
  /** Closes the question without sending ("Review HHT orders" navigates as well). */
  dismiss: () => void;
  reset: () => void;
}

// Whether a Default VC cart lookup is in flight, and a generation `reset` bumps so a lookup that
// started before it cannot ask or send afterwards.
let lookingUp = false;
let generation = 0;

export const useHhtSendGuardStore = create<HhtSendGuardState>((set, get) => ({
  request: null,

  async guard(cart, send, options = {}) {
    // One send decision at a time: a press while a lookup runs is ignored.
    if (lookingUp) return false;
    const { applies, waitingCount } = useHhtWaitingCountStore.getState();
    if (!applies || waitingCount <= 0) {
      send();
      return false;
    }
    const ask = (): true => {
      set({
        request: {
          waitingCount,
          sendAnyway: send,
          ...(options.onDismiss !== undefined ? { onDismiss: options.onDismiss } : {}),
        },
      });
      return true;
    };
    const entries = useSessionStore.getState().orderingChoices?.entries ?? [];
    if (isHhtGuardedDepot(entries, cart.depotId)) return ask();
    // The Default VC cart is a Value Centre's `DEPOT` cart keyed by an account, so a cart with no
    // account chosen yet or of another entry kind is never it.
    const { customerAccountCode } = cart;
    if (customerAccountCode === null || cart.entryKind !== 'DEPOT') {
      send();
      return false;
    }
    lookingUp = true;
    const started = generation;
    let isDefaultValueCentreCart: boolean;
    try {
      const defaultValueCentreCart = await getHhtDefaultValueCentreCart();
      isDefaultValueCentreCart =
        defaultValueCentreCart !== null &&
        defaultValueCentreCart.depotId === cart.depotId &&
        defaultValueCentreCart.customerAccountCode === customerAccountCode;
    } catch (error) {
      console.warn(
        `HHT send guard: the Default VC cart lookup failed, so the send asks first (${error instanceof Error ? error.message : 'unknown error'})`,
      );
      isDefaultValueCentreCart = true;
    } finally {
      if (started === generation) lookingUp = false;
    }
    if (started !== generation) return false;
    if (isDefaultValueCentreCart) return ask();
    send();
    return false;
  },

  sendAnyway() {
    const { request } = get();
    set({ request: null });
    request?.sendAnyway();
  },

  dismiss() {
    const { request } = get();
    set({ request: null });
    request?.onDismiss?.();
  },

  reset() {
    generation += 1;
    lookingUp = false;
    set({ request: null });
  },
}));
